/**
 * Analytics utility for tracking authentication events
 * Supports Google Analytics, custom analytics, and console logging
 */

export interface AuthEvent {
  event: string;
  method: "email" | "phone" | "google" | "apple";
  category: "authentication" | "oauth" | "password";
  label?: string;
  value?: number;
}

class Analytics {
  private isGAEnabled(): boolean {
    return (
      typeof window !== "undefined" &&
      typeof (window as any).gtag === "function"
    );
  }

  /**
   * Track authentication events
   */
  trackAuthEvent(event: AuthEvent) {
    const { event: eventName, method, category, label, value } = event;

    // Google Analytics
    if (this.isGAEnabled()) {
      (window as any).gtag("event", eventName, {
        method,
        event_category: category,
        event_label: label || `${method}_${eventName}`,
        value,
      });
    }

    // Custom analytics endpoint (if you have one)
    this.sendToCustomAnalytics(event);

    // Console log for development
    if (process.env.NODE_ENV === "development") {
      console.log("Analytics Event:", event);
    }
  }

  /**
   * Track OAuth events specifically
   */
  trackOAuthEvent(
    provider: "google" | "apple",
    event: string,
    success: boolean = true
  ) {
    this.trackAuthEvent({
      event: success ? `${event}_success` : `${event}_failed`,
      method: provider,
      category: "oauth",
      label: `${provider}_${event}`,
    });
  }

  /**
   * Track login events
   */
  trackLogin(method: "email" | "phone" | "google" | "apple", success: boolean) {
    this.trackAuthEvent({
      event: success ? "login_success" : "login_failed",
      method,
      category: "authentication",
      label: `${method}_login`,
    });
  }

  /**
   * Track signup events
   */
  trackSignup(
    method: "email" | "phone" | "google" | "apple",
    success: boolean
  ) {
    this.trackAuthEvent({
      event: success ? "signup_success" : "signup_failed",
      method,
      category: "authentication",
      label: `${method}_signup`,
    });
  }

  /**
   * Track password events
   */
  trackPasswordEvent(
    event: "reset" | "change" | "migration",
    success: boolean
  ) {
    this.trackAuthEvent({
      event: success ? `${event}_success` : `${event}_failed`,
      method: "email", // Password events are typically email-based
      category: "password",
      label: `password_${event}`,
    });
  }

  /**
   * Send to custom analytics endpoint
   */
  private async sendToCustomAnalytics(event: AuthEvent) {
    try {
      // Replace with your analytics endpoint
      const analyticsEndpoint = process.env.REACT_APP_ANALYTICS_ENDPOINT;
      if (analyticsEndpoint) {
        await fetch(analyticsEndpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...event,
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
            url: window.location.href,
          }),
        });
      }
    } catch (error) {
      // Silently fail analytics
      console.error("Analytics error:", error);
    }
  }
}

export const analytics = new Analytics();

// Convenience functions
export const trackOAuthEvent = (
  provider: "google" | "apple",
  event: string,
  success: boolean = true
) => {
  analytics.trackOAuthEvent(provider, event, success);
};

export const trackLogin = (
  method: "email" | "phone" | "google" | "apple",
  success: boolean
) => {
  analytics.trackLogin(method, success);
};

export const trackSignup = (
  method: "email" | "phone" | "google" | "apple",
  success: boolean
) => {
  analytics.trackSignup(method, success);
};
